import type {
  ApolloCache,
  DefaultContext,
  DocumentNode,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum'; // TODO: SAV-3878 build react-native support

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

function getOperationName(document: DocumentNode) {
  const definition = document.definitions[0];

  if (definition === undefined || !('name' in definition)) {
    return undefined;
  }

  return definition.name?.value;
}

type ErrorReporting = {
  /**
   * Set false for a fire-and-forget mutation whose failure is not a product
   * defect. A RUM error raised inside a click's activity window tags that
   * click `error_click`, so a dropped analytics beacon counts against the
   * product-quality KR even though nothing the user sees is broken.
   *
   * Opting out means you own the signal: report the failure some other way
   * (`options.onError`) rather than dropping it.
   */
  readonly reportToRum: boolean;
};

/**
 * @deprecated Use useMutationWithErrorLogging from @savvy/graphql-hooks instead
 */
export function useMutationWithErrorLogging<
  TData = unknown,
  TVariables = OperationVariables,
  TContext = DefaultContext,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables, TContext>,
  errorReporting: ErrorReporting = { reportToRum: true },
): MutationTuple<TData, TVariables, TContext, ApolloCache<unknown>> {
  const resp = useMutation(mutation, options);
  const advisorId = loggedInAsAdvisorID();
  const auth = useAuth();

  useEffect(() => {
    if (resp[1].error && errorReporting.reportToRum) {
      try {
        throw new Error(
          `${getOperationName(mutation)}
          error_name=${resp[1].error.name} 
          error_message=${resp[1].error.message}`,
        );
      } catch (error) {
        console.error(error);
        try {
          datadogRum.addError(error, {
            user_id: auth.user?.id,
            logged_in_as_user_id: advisorId,
          });
        } catch (rumError) {
          // @datadog/browser-rum is web-only (SAV-3878) and throws on native. Logging here
          // surfaces genuine web-side RUM failures instead of silently swallowing them.
          console.error('[datadog-rum]', rumError);
        }
      }
    }
  }, [resp[1].error]);
  return resp;
}
